import { route } from "./routes.js";
import { rootShellHostSchema } from "./root-shell.js";
import { z } from "zod";
//#region src/org.ts
/**
* `/v1/admin/org*` — the five organization-settings routes the admin app calls.
*
* Producer: `AdminOrgController` and `OrgService` in `cloud/dp/core-api/src/admin/`. Consumer: the
* admin app's org settings page, and `getOrgSettings` is also called from non-admin shell code
* today. The shell's read types differ from the controller in three places:
*
* 1. `organizationPublicColumns` projects 12 columns. The shell's `OrgSettings` declares six and
*    omits `settings`, `allowedDomains`, `domainMappings`, `status`, `plan` and `updatedAt` — all
*    of which `GET` and `PATCH` return.
* 2. `verifyDomain` returns `resolvedTo: string | null`; the shell's `DomainVerifyResult` declares
*    it non-nullable, and the service returns `null` on every failure arm.
* 3. `getDomainStatus` returns `{ status, domain }`. The shell's `DomainStatus` declares
*    `{ status, message? }` — no `domain` key at all, a `message` key the server never sends, and
*    a three-value union for `status` that omits the `'none'` the service returns for an org with
*    no custom domain.
*
* Permission keys: `settings:read` on `GET /v1/admin/org` and `GET /v1/admin/org/domain-status`,
* `settings:manage` on the patch and on `POST /v1/admin/org/verify-domain`, and `org:manage` on
* `POST /v1/admin/org/archive`.
*
* `branding` is the org's shell branding blob. `@zion/sdk-cp-dp-contracts` owns an `OrgBranding`
* type, but this package depends only on `@zion/sdk-contracts` and zod, so the column is modelled
* as the `jsonb` record it is. The column is `NOT NULL` with a `{}` default, so it never arrives
* null — the shell's `OrgBranding | null` is wrong in the nullable direction.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped rather than refused and one new column cannot red every page that reads the route.
* Every request schema is `z.strictObject`, so a key the server would reject is refused before
* the request leaves the client. No shape in this file travels in both directions.
*/
/**
* The 12 `organizationPublicColumns` — the body of `GET` and `PATCH /v1/admin/org`.
*
* Secret-free by construction: `signingKeys` holds the org's RS256 keypair, whose private half
* signs `data_plane` JWTs, and it is excluded at the projection rather than blocklisted at a
* handler. Do not add it here.
*
* `settings`, `branding` and `domainMappings` are `jsonb` columns, each `NOT NULL` with a `{}`
* default. `allowedDomains` is a `text[]`, `NOT NULL` with a `{}` default, and drives Google OAuth
* just-in-time provisioning. `status` is `'active'` or `'archived'`; `plan` defaults to
* `'starter'`.
*
* Owner: `organizationPublicColumns` in
* `cloud/dp/core-api/src/db/schema/auth/organizations.ts`.
*/
const orgSettingsSchema = z.object({
	id: z.uuid(),
	slug: z.string(),
	name: z.string(),
	settings: z.record(z.string(), z.unknown()),
	branding: z.record(z.string(), z.unknown()),
	allowedDomains: z.array(z.string()),
	customDomain: z.string().nullable(),
	domainMappings: z.record(z.string(), z.unknown()),
	status: z.string(),
	plan: z.string(),
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime()
});
/** `GET /v1/admin/org` — the organization record. Requires `settings:read`. */
const GET_ORG_ROUTE = route("GET", "/v1/admin/org");
/**
* `PATCH /v1/admin/org` — update name, settings, allowed domains or custom domain. Requires
* `settings:manage`.
*/
const UPDATE_ORG_ROUTE = route("PATCH", "/v1/admin/org");
/**
* `POST /v1/admin/org/verify-domain` — resolve the custom domain's CNAME and report whether it
* points here. Requires `settings:manage`.
*/
const VERIFY_ORG_DOMAIN_ROUTE = route("POST", "/v1/admin/org/verify-domain");
/**
* `GET /v1/admin/org/domain-status` — the custom domain's current state. Requires
* `settings:read`.
*/
const GET_ORG_DOMAIN_STATUS_ROUTE = route("GET", "/v1/admin/org/domain-status");
/**
* `POST /v1/admin/org/archive` — archive the caller's organization. Status 201, Nest's default
* for a `@Post` with no `@HttpCode`; `openapi.json` documents 200, so a client accepts any 2xx.
* The body is `{ message: 'Organization archived' }`. Requires `org:manage`; the producer checks
* no owner role beyond that key.
*
* Errors: 400 `Confirmation name does not match organization name` when `confirmName` differs
* from the organization's `name`, compared exactly; 400 `Organization is already archived`. On
* success the organization's `status` becomes `'archived'` and an `org_archived` audit row is
* written. `OrgService` has no method that sets `status` back to `'active'`.
*/
const ARCHIVE_ORG_ROUTE = route("POST", "/v1/admin/org/archive");
/**
* The body of `GET /v1/admin/org`: the 12 columns, plus `canonicalHost`, the host of the org's
* token issuer: the address its users open. It uses the hostname grammar of the root-shell rows.
* The key is optional, so an admin app reads a DP from before the field; a DP that sends it may send null.
*/
const getOrgSettingsResponseSchema = orgSettingsSchema.extend({ canonicalHost: rootShellHostSchema.nullable().optional() });
/**
* The body of `PATCH /v1/admin/org`.
*
* Four optional keys, and `OrgService.updateOrg` writes only the ones present — an absent key
* leaves the column alone. `customDomain` is nullable so a caller can clear it; the other three
* are not, so `null` is refused rather than written.
*
* No `slug`: the slug is immutable and the update builds its patch from these four keys only.
*
* Owner: `updateOrgSchema` in `cloud/dp/core-api/src/admin/admin-org.controller.ts`.
*/
const updateOrgSettingsRequestSchema = z.strictObject({
	name: z.string().optional(),
	settings: z.record(z.string(), z.unknown()).optional(),
	allowedDomains: z.array(z.string()).optional(),
	customDomain: z.string().nullable().optional()
});
/** The body of `PATCH /v1/admin/org` — the saved row: the 12 columns, without the read's `canonicalHost`. */
const updateOrgSettingsResponseSchema = orgSettingsSchema;
/**
* The body of `POST /v1/admin/org/verify-domain`.
*
* `resolvedTo` is the first CNAME record the resolver returned, and `null` on all three failure
* arms: no custom domain configured, an empty record set, and a DNS error. `verified` is true only
* when some record equals `<slug>.zion.witness.ai`, so `verified: false` with a non-null
* `resolvedTo` is the "points somewhere else" case an admin needs to see.
*
* Owner: `OrgService.verifyDomain`.
*/
const verifyOrgDomainResponseSchema = z.object({
	verified: z.boolean(),
	resolvedTo: z.string().nullable()
});
/**
* The body of `GET /v1/admin/org/domain-status`.
*
* `status` is `'none'` when the org has no custom domain, and otherwise `'ready'` or `'pending'`
* from the same CNAME check `POST /v1/admin/org/verify-domain` runs. `domain` is the configured
* custom domain, and `null` exactly when `status` is `'none'`.
*
* The union is closed because `OrgService.getDomainStatus` returns one of those three literals on
* every path, even though it declares its return as a bare `string`.
*
* Owner: `OrgService.getDomainStatus`.
*/
const getOrgDomainStatusResponseSchema = z.object({
	status: z.enum([
		"none",
		"pending",
		"ready"
	]),
	domain: z.string().nullable()
});
/**
* The body of `POST /v1/admin/org/archive`.
*
* `confirmName` is the organization's display name, typed by the caller as a confirmation. The
* server compares it to the stored `name` byte for byte and declares a bare `z.string()`. The
* contract adds no bounds (zion-admin-app `uncovered-admin-routes` ruling R-006):
* {@link updateOrgSettingsRequestSchema} leaves `name` unbounded, so an organization may hold an
* empty name or one past 256 characters, and a bounded `confirmName` would leave that
* organization with no way to be archived.
*
* Bound directly by `AdminOrgController.archiveOrg` in
* `cloud/dp/core-api/src/admin/admin-org.controller.ts` since #3331.
*/
const archiveOrgRequestSchema = z.strictObject({ confirmName: z.string() });
/** The body of `POST /v1/admin/org/archive` — `{ message: 'Organization archived' }`. */
const archiveOrgResponseSchema = z.object({ message: z.string() });
/** Every route this file names, for `route-census.ts`. */
const orgRoutes = Object.freeze([
	GET_ORG_ROUTE,
	UPDATE_ORG_ROUTE,
	VERIFY_ORG_DOMAIN_ROUTE,
	GET_ORG_DOMAIN_STATUS_ROUTE,
	ARCHIVE_ORG_ROUTE
]);
//#endregion
export { ARCHIVE_ORG_ROUTE, GET_ORG_DOMAIN_STATUS_ROUTE, GET_ORG_ROUTE, UPDATE_ORG_ROUTE, VERIFY_ORG_DOMAIN_ROUTE, archiveOrgRequestSchema, archiveOrgResponseSchema, getOrgDomainStatusResponseSchema, getOrgSettingsResponseSchema, orgRoutes, orgSettingsSchema, updateOrgSettingsRequestSchema, updateOrgSettingsResponseSchema, verifyOrgDomainResponseSchema };

//# sourceMappingURL=org.js.map